import React from "react";
import { Pencil, Check, X } from "lucide-react";

const INPUT_BASE_CLASS =
  "h-11 w-full rounded-xl border border-gray-200 bg-white px-4 text-sm font-inter text-gray-900 outline-none transition-colors placeholder:text-gray-400 focus:border-primary-blue";

export default function AccountSettingRow({
  label,
  fieldKey,
  value,
  readOnly,
  isEditing,
  editingValue,
  saving,
  fieldError,
  rowRef,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onEditingValueChange,
}) {
  return (
    <div
      ref={isEditing ? rowRef : null}
      className="grid grid-cols-1 md:grid-cols-[200px_1fr_auto] items-start md:items-center py-4 px-5 gap-3 md:gap-4 border-t border-gray-100 font-inter text-sm text-gray-700 hover:bg-blue-50/30 transition-colors overflow-visible"
    >
      <div className="flex items-center justify-between gap-3 md:block">
        <p className="text-xs md:text-sm font-medium text-gray-500 uppercase md:normal-case tracking-wider md:tracking-normal">
          {label}
        </p>
        {readOnly && (
          <span className="inline-flex md:hidden rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-medium text-gray-500">
            Read only
          </span>
        )}
      </div>

      {isEditing ? (
        <div className="flex items-center gap-3 min-w-0">
          <input
            autoFocus
            type="text"
            value={editingValue}
            onChange={(e) => onEditingValueChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") onSaveEdit(fieldKey);
              if (e.key === "Escape") onCancelEdit();
            }}
            className={`flex-1 min-w-0 rounded-xl px-4 py-2.5 text-sm text-gray-900 font-medium outline-none transition-colors ${
              fieldError ? "border border-red-400 focus:border-red-400" : INPUT_BASE_CLASS
            }`}
          />
          {fieldError && (
            <p className="text-xs text-red-500 whitespace-nowrap flex-shrink-0">{fieldError}</p>
          )}
        </div>
      ) : (
        <div className="rounded-xl border border-gray-100 bg-gray-50/70 px-4 py-3">
          <p className="font-medium text-gray-900 break-words">{value || <span className="text-gray-400">—</span>}</p>
        </div>
      )}

      <div className="flex items-center justify-end md:justify-center w-full md:w-16">
        {!readOnly && (
          <>
            <div className={`overflow-hidden transition-all duration-200 ${isEditing ? "max-w-0 opacity-0" : "max-w-[2rem] opacity-100"}`}>
              <button
                onClick={() => onStartEdit(fieldKey, value)}
                className="p-1.5 rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
                aria-label={`Edit ${label}`}
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className={`overflow-hidden transition-all duration-200 flex items-center gap-2 ${isEditing ? "max-w-[5rem] opacity-100" : "max-w-0 opacity-0"}`}>
              <button
                onClick={() => onSaveEdit(fieldKey)}
                disabled={saving}
                className="p-1.5 rounded-md text-white bg-primary-blue hover:bg-blue-700 disabled:opacity-50 transition-colors"
                aria-label="Save"
              >
                <Check className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={onCancelEdit}
                className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 transition-colors"
                aria-label="Cancel"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
